import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { NAVIGATION_SIDEBAR_MARKS } from '@atlassian/jira-atlassian-navigation-performance-metrics/src/constants.tsx';
import {
	NavSidebarPerformanceMark,
	NavSidebarPerformanceReport,
} from '@atlassian/jira-atlassian-navigation-performance-metrics/src/index.tsx';
import { useExperienceFail } from '@atlassian/jira-experience-tracker/src/ui/experience-fail/index.tsx';
import { useExperienceStart } from '@atlassian/jira-experience-tracker/src/ui/experience-start/index.tsx';
import { useExperienceSuccess } from '@atlassian/jira-experience-tracker/src/ui/experience-success/index.tsx';
import { createValueController } from '@atlassian/jira-platform-controllers-value/src/index.tsx';
import { useCurrentRoute } from '@atlassian/jira-platform-router-utils/src/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { usePageId } from '@atlassian/jira-spa-state-controller/src/components/use-page-id/index.tsx';
import { useRouter } from '@atlassian/react-resource-router';
import { SIDEBAR_EXPERIENCE } from '../../common/constants.tsx';
import type { SIDEBAR_ID } from './constants.tsx';

type SidebarId = (typeof SIDEBAR_ID)[keyof typeof SIDEBAR_ID];
type SidebarReadyProps = {
	id: SidebarId;
};

// @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
const { useValue: useSidebarId } = createValueController<SidebarId | undefined>('sidebar-id');

const EXPERIENCE_SUCCESS_ATTRIBUTES = {
	experience: SIDEBAR_EXPERIENCE,
	attributes: {
		sidebar: 'v3',
	},
} as const;

export const SidebarStart = () => {
	const [{ route, match }] = useRouter();
	const pageId = usePageId();
	const startRef = useRef(Date.now());
	const onStartExperience = useExperienceStart({
		experienceId: pageId,
		experience: SIDEBAR_EXPERIENCE,
		analyticsSource: 'Sidebar',
	});
	useMemo(() => {
		if (route.isRedirect ?? false) return;

		onStartExperience();
		startRef.current = Date.now();

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [route, match]);

	return null;
};

export const SidebarRenderStart = (): React.JSX.Element => (
	<NavSidebarPerformanceMark metricKey={NAVIGATION_SIDEBAR_MARKS.renderStart} />
);

export const SidebarReady = ({ id }: SidebarReadyProps): React.JSX.Element => {
	const [, setSidebarId] = useSidebarId();
	const route = useCurrentRoute();

	const routeName = route.name ?? route.group ?? 'UNKNOWN';
	const onSuccess = useExperienceSuccess(EXPERIENCE_SUCCESS_ATTRIBUTES);

	useEffect(() => {
		setSidebarId(id);
		// @ts-expect-error - TS2554 - Expected 1 arguments, but got 0.
		return () => setSidebarId();
	}, [setSidebarId, id]);

	useLayoutEffect(() => {
		if (route.isRedirect ?? false) return;
		onSuccess();
	}, [onSuccess, route]);

	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		// Why are we injecting the route name into the global window state?
		// We currently don't have any way to get the route name when the sidenav is rendered OUTSIDE of the SPA

		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		window.__POLLINATOR__ = { ...window.__POLLINATOR__, routeName };

		const event = createAnalyticsEvent({
			action: 'ready',
			actionSubject: 'sidebar',
			meta: { teamName: 'endeavour' },
		});

		fireUIAnalytics(event, 'Sidebar Ready', {
			route: routeName,
		});
	}, [createAnalyticsEvent, routeName]);

	const customData = useMemo(
		() => ({
			route: routeName,
			sidebarId: id,
		}),
		[routeName, id],
	);

	return (
		<>
			<NavSidebarPerformanceMark metricKey={NAVIGATION_SIDEBAR_MARKS.renderEnd} />
			<NavSidebarPerformanceReport customData={customData} />
		</>
	);
};

export const useSidebarFail = () => {
	const onFail = useExperienceFail({
		experience: SIDEBAR_EXPERIENCE,
	});

	return useCallback(
		(location: string, error: Error) => {
			onFail(location, error);
		},
		[onFail],
	);
};

export const useSidebarSuccess = () => useExperienceSuccess(EXPERIENCE_SUCCESS_ATTRIBUTES);
